import { EltPeriodCell } from 'legacy/lut/components/widgets/public/partner360/ELT'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations'
import { NotificationController } from 'core-components/library/popup'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'

import { IFinancialCockpitQbrDocument } from '../../../../api/types'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { formatCockpitActualsDate } from '../../../../lib'
import {
  buildQbrDocumentOpenLink,
  canDownloadQbrPptx,
  getQbrDocumentDisplayName,
  parseQbrDocumentSettings,
} from '../../../../lib/documentsAndLinks'
import { deleteQbrDocument, downloadQbrDocumentPptx } from '../../../../lib/documentsAndLinks/api'
import { removeCockpitQbrDocument } from '../../../../model'
import { CockpitTable, ICockpitTableColumn } from '../../../components/cockpitTable'
import { DocumentActions } from '../documentActions/DocumentActions'

import styles from './QbrDocumentsTable.module.scss'

interface IQbrDocumentsTableProps {
  partnerId: string
  documents: IFinancialCockpitQbrDocument[]
  readOnly: boolean
  showPartnerId?: boolean
  getPartnerId?: (document: IFinancialCockpitQbrDocument) => string
}

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.documentsAndLinks

export const QbrDocumentsTable = ({
  partnerId,
  documents,
  readOnly,
  getPartnerId,
  showPartnerId = false,
}: IQbrDocumentsTableProps) => {
  const { isSuperAdmin } = useUserRoles()
  const dispatch = useAppDispatch()
  const user = useSelector(selectUser)
  const settingsByDocument = new Map(
    documents.map((document) => [document, parseQbrDocumentSettings(document.Settings)])
  )

  const columns: ICockpitTableColumn<IFinancialCockpitQbrDocument>[] = [
    {
      key: showPartnerId ? 'partnerId' : 'name',
      header: (
        <Translation span text={showPartnerId ? KEYS.columns.partnerId : KEYS.columns.name} />
      ),
      renderCell: (document) =>
        showPartnerId
          ? (getPartnerId?.(document) ?? partnerId)
          : getQbrDocumentDisplayName(document),
    },
    {
      key: 'period',
      header: <Translation span text={KEYS.columns.period} />,
      renderCell: (document) => {
        const periods = settingsByDocument
          .get(document)
          ?.selectedPeriods?.filter(({ fYear }) => fYear === document.FYear)

        return periods?.length ? (
          <div>
            {periods.map(({ fYear, fQuarters }, index) => (
              <EltPeriodCell
                key={`${fYear}-${index}`}
                fYear={fYear ?? null}
                fQuarters={fQuarters}
              />
            ))}
          </div>
        ) : (
          <EltPeriodCell
            fYear={document.FYear ?? null}
            fQuarters={document.FQuarter ? [document.FQuarter] : []}
          />
        )
      },
    },
    {
      key: 'netNdp',
      header: <Translation span text={KEYS.columns.netNdp} />,
      renderCell: (document) => settingsByDocument.get(document)?.selectedNetNdp ?? '—',
    },
    {
      key: 'currency',
      header: <Translation span text={KEYS.columns.currency} />,
      renderCell: (document) => settingsByDocument.get(document)?.selectedCurrency ?? '—',
    },
    {
      key: 'offerings',
      header: <Translation span text={KEYS.columns.offerings} />,
      renderCell: (document) => {
        const offerings = settingsByDocument.get(document)?.selectedOfferings

        return offerings?.length ? offerings.join(', ') : '—'
      },
    },
    {
      key: 'legacy',
      header: <Translation span text={KEYS.columns.legacy} />,
      renderCell: (document) => (
        <Translation
          span
          text={document.IsLegacyDocument === 1 ? KEYS.legacy.yes : KEYS.legacy.no}
        />
      ),
    },
    {
      key: 'status',
      header: <Translation span text={KEYS.columns.status} />,
      renderCell: (document) => (
        <Translation
          span
          text={document.Finalized === 1 ? KEYS.status.finalized : KEYS.status.draft}
        />
      ),
    },
    {
      key: 'lastModified',
      header: <Translation span text={KEYS.columns.lastModified} />,
      renderCell: (document) =>
        document.Modified ? formatCockpitActualsDate(document.Modified) : '—',
    },
    {
      key: 'modifiedBy',
      header: <Translation span text={KEYS.columns.modifiedBy} />,
      renderCell: (document) => document.ModifiedBy ?? '—',
    },
    {
      key: 'actions',
      header: null,
      renderCell: (document) => (
        <DocumentActions
          openHref={buildQbrDocumentOpenLink(document.EntryId)}
          documentLabel={getQbrDocumentDisplayName(document)}
          canDownload={canDownloadQbrPptx(document)}
          onDownload={() => downloadQbrDocumentPptx(document)}
          readOnly={readOnly}
          canDelete={document.CreatedBy === user.email || isSuperAdmin}
          onDelete={() => deleteQbrDocument(document.EntryId)}
          onDeleted={() => {
            dispatch(
              removeCockpitQbrDocument({
                partnerId: getPartnerId?.(document) ?? partnerId,
                entryId: document.EntryId,
              })
            )
            NotificationController.showSuccess({ message: 'QBR document deleted successfully.' })
          }}
          onDeleteError={() => {
            NotificationController.showError({ message: 'Failed to delete the QBR document.' })
          }}
        />
      ),
    },
  ]

  if (documents.length === 0) {
    return (
      <div className={styles.empty}>
        <Translation span text={KEYS.emptyQbr} />
      </div>
    )
  }

  return (
    <CockpitTable
      columns={columns}
      items={documents}
      getItemKey={(document) => `${getPartnerId?.(document) ?? partnerId}:${document.EntryId}`}
    />
  )
}

QbrDocumentsTable.displayName = 'QbrDocumentsTable'
